import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowLeft } from "lucide-react";
import DiagramView from "@/components/DiagramView";

export default function PracticePrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [plays, setPlays] = useState([]);
  const [drills, setDrills] = useState([]);
  const [roster, setRoster] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: practice }, { data: allPlays }, { data: allDrills }] = await Promise.all([
          api.get(`/practices/${id}`),
          api.get(`/plays`),
          api.get(`/drills`),
        ]);
        setP(practice);
        setPlays(allPlays);
        setDrills(allDrills);
      } catch (e) { setErr("Practice not found"); }
      // Roster is optional — attendance sign-in section only renders if we have it
      try {
        const { data } = await api.get(`/roster`);
        setRoster(data);
      } catch (e) { console.error("Roster unavailable; sign-in section hidden", e); }
    })();
  }, [id]);

  useEffect(() => {
    // Auto-print once both the practice and (attempted) roster fetch have settled
    if (p) {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [p, roster]);

  const playById = useMemo(() => Object.fromEntries(plays.map(pl => [pl.id, pl])), [plays]);
  const drillById = useMemo(() => Object.fromEntries(drills.map(dr => [dr.id, dr])), [drills]);

  if (err) return <div className="p-8 text-red-400">{err}</div>;
  if (!p) return <div className="p-8 text-slate-400">Loading…</div>;

  const total = (p.periods || []).reduce((s, x) => s + (+x.duration || 0), 0);
  const addMin = (hhmm, mins) => {
    const [h, m] = hhmm.split(":").map(Number);
    const totalM = h * 60 + m + mins;
    return String(Math.floor(totalM / 60) % 24).padStart(2, "0") + ":" + String(totalM % 60).padStart(2, "0");
  };

  // Build enriched period list with running clock and linked plays/drills
  let cursor = p.start_time || "15:30";
  const periods = (p.periods || []).map(pd => {
    const start = cursor;
    cursor = addMin(cursor, +pd.duration || 0);
    const linkedPlays = (pd.play_ids || []).map(pid => playById[pid]).filter(Boolean);
    const linkedDrills = (pd.drill_ids || []).map(did => drillById[did]).filter(Boolean);
    return { ...pd, start, linkedPlays, linkedDrills };
  });

  return (
    <div className="practice-print min-h-screen bg-white text-black">
      {/* Screen-only toolbar */}
      <div className="print-hide bg-[#0B0D12] text-white border-b border-surface-3 px-8 py-4 flex items-center justify-between">
        <button onClick={() => nav(-1)} data-testid="print-back" className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber font-condensed uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="font-condensed uppercase tracking-widest text-sm text-amber">Print Preview · Practice Card</div>
        <button onClick={() => window.print()} data-testid="print-btn" className="bg-amber hover:bg-amber-600 text-[#0B0D12] px-4 py-2 font-condensed uppercase tracking-widest text-xs flex items-center gap-2">
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      {/* Printable card */}
      <div className="practice-card mx-auto my-6 bg-white text-black shadow-lg print:shadow-none print:my-0" style={{ maxWidth: "820px", padding: "40px 44px" }}>
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-black pb-4">
          <div>
            <div className="font-display text-4xl tracking-wider uppercase leading-none">{user?.program_name || "Your Program"}</div>
            <div className="font-condensed uppercase tracking-[0.35em] text-xs mt-1">Practice Card</div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl leading-none">{p.date}</div>
            <div className="font-condensed uppercase tracking-widest text-xs mt-1">{p.start_time} · {total} min</div>
          </div>
        </div>

        {/* Meta */}
        <div className="mt-5">
          <div className="font-display text-3xl uppercase tracking-wide leading-tight">{p.title}</div>
          <div className="grid grid-cols-3 gap-6 mt-4 text-sm">
            <div>
              <div className="text-[9px] font-condensed uppercase tracking-[0.28em] text-neutral-500">Location</div>
              <div className="mt-1">{p.location || "—"}</div>
            </div>
            <div>
              <div className="text-[9px] font-condensed uppercase tracking-[0.28em] text-neutral-500">Weather</div>
              <div className="mt-1">{p.weather || "—"}</div>
            </div>
            <div>
              <div className="text-[9px] font-condensed uppercase tracking-[0.28em] text-neutral-500">Attendance</div>
              <div className="mt-1">{p.attendance_expected || 0} expected</div>
            </div>
          </div>
          {p.objectives && (
            <div className="mt-4 border-l-4 border-black pl-3">
              <div className="text-[9px] font-condensed uppercase tracking-[0.28em] text-neutral-500">Objectives</div>
              <div className="mt-1 text-sm whitespace-pre-wrap">{p.objectives}</div>
            </div>
          )}
        </div>

        {/* Timeline table */}
        <div className="mt-6">
          <div className="font-display text-xl uppercase tracking-wider border-b-2 border-black pb-1">Timeline</div>
          <table className="w-full mt-3 text-sm border-collapse">
            <thead>
              <tr className="border-b border-black text-left">
                <th className="text-[9px] font-condensed uppercase tracking-widest py-2 pr-2 w-14">Time</th>
                <th className="text-[9px] font-condensed uppercase tracking-widest py-2 pr-2 w-14">Min</th>
                <th className="text-[9px] font-condensed uppercase tracking-widest py-2 pr-2">Period</th>
                <th className="text-[9px] font-condensed uppercase tracking-widest py-2 pr-2 w-40">Coach</th>
                <th className="text-[9px] font-condensed uppercase tracking-widest py-2 pr-2 w-32">Equipment</th>
                <th className="text-[9px] font-condensed uppercase tracking-widest py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((pd, i) => (
                <tr key={pd.id || i} className="border-b border-neutral-300 align-top">
                  <td className="py-2 pr-2 font-mono text-xs">{pd.start}</td>
                  <td className="py-2 pr-2 font-mono text-xs">{pd.duration}</td>
                  <td className="py-2 pr-2 font-semibold">{pd.name}</td>
                  <td className="py-2 pr-2 text-xs">{pd.coach}</td>
                  <td className="py-2 pr-2 text-xs">{pd.equipment}</td>
                  <td className="py-2 text-xs">{pd.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Play install — diagrams for linked plays */}
        {periods.some(pd => pd.linkedPlays.length > 0) && (
          <div className="mt-8 practice-card-plays" data-testid="print-play-install">
            <div className="font-display text-xl uppercase tracking-wider border-b-2 border-black pb-1">Play Install</div>
            <div className="mt-4 grid grid-cols-2 gap-6">
              {periods.flatMap(pd =>
                pd.linkedPlays.map(pl => (
                  <div key={`${pd.id}-${pl.id}`} className="border border-neutral-400 p-3 avoid-page-break" data-testid={`print-play-${pl.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[9px] font-condensed uppercase tracking-[0.28em] text-neutral-500">
                          {pd.name} · {pd.start}
                        </div>
                        <div className="font-display text-lg uppercase tracking-wide leading-tight mt-0.5">{pl.name}</div>
                        <div className="text-[10px] font-condensed uppercase tracking-widest text-neutral-600 mt-0.5">
                          {(pl.side || "").replace("_"," ")} · {pl.formation || "—"} · {pl.personnel || ""}
                        </div>
                      </div>
                    </div>
                    {pl.diagram && (pl.diagram.positions?.length || pl.diagram.routes?.length) ? (
                      <div className="mt-2 border border-neutral-300">
                        <DiagramView diagram={pl.diagram} printMode />
                      </div>
                    ) : (
                      <div className="mt-2 text-[10px] font-condensed uppercase tracking-widest text-neutral-500 italic border border-dashed border-neutral-300 p-3 text-center">
                        No diagram — see assignments
                      </div>
                    )}
                    {pl.assignments && (
                      <div className="mt-2 text-[10px] leading-snug whitespace-pre-wrap">
                        {pl.assignments.slice(0, 240)}{pl.assignments.length > 240 ? "…" : ""}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Drill install — coaching points for linked drills */}
        {periods.some(pd => pd.linkedDrills.length > 0) && (
          <div className="mt-8 practice-card-drills" data-testid="print-drill-install">
            <div className="font-display text-xl uppercase tracking-wider border-b-2 border-black pb-1">Drill Card</div>
            <div className="mt-4 grid grid-cols-2 gap-6">
              {periods.flatMap(pd =>
                pd.linkedDrills.map(dr => (
                  <div key={`${pd.id}-${dr.id}`} className="border border-neutral-400 p-3 avoid-page-break" data-testid={`print-drill-${dr.id}`}>
                    <div className="text-[9px] font-condensed uppercase tracking-[0.28em] text-neutral-500">
                      {pd.name} · {pd.start} · {dr.duration_min || 10} min
                    </div>
                    <div className="font-display text-lg uppercase tracking-wide leading-tight mt-0.5">{dr.name}</div>
                    <div className="text-[10px] font-condensed uppercase tracking-widest text-neutral-600 mt-0.5">
                      {(dr.category || "general").replace("_", " ")} · {dr.difficulty || "medium"} · {(dr.age_level || "varsity").toUpperCase()}
                    </div>
                    {dr.purpose && (
                      <div className="mt-2 text-[11px] italic leading-snug">
                        Purpose: {dr.purpose}
                      </div>
                    )}
                    {dr.coaching_points && (
                      <div className="mt-2 text-[10px] leading-snug whitespace-pre-wrap">
                        <span className="font-semibold">Coaching Points:</span>
                        {"\n"}{dr.coaching_points.slice(0, 260)}{dr.coaching_points.length > 260 ? "…" : ""}
                      </div>
                    )}
                    {dr.equipment && (
                      <div className="mt-2 text-[10px] leading-snug">
                        <span className="font-semibold">Equipment:</span> {dr.equipment}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Attendance sign-in — page-break before so field staff can print just this page */}
        {roster && roster.players && roster.players.length > 0 && (
          <AttendanceSignIn roster={roster} attendance={p.attendance || {}} />
        )}

        {/* Footer */}
        <div className="mt-8 pt-3 border-t border-black flex items-center justify-between text-[10px] font-condensed uppercase tracking-widest text-neutral-600">
          <div>Gridiron OS · Practice Card</div>
          <div>Standards. Toughness. Brotherhood. Relentless. Humility.</div>
        </div>
      </div>
    </div>
  );
}

function AttendanceSignIn({ roster, attendance }) {
  const positionsOrder = [
    ...(roster.positions?.standard || []),
    ...(roster.positions?.custom || []),
  ];
  const grouped = {};
  for (const p of roster.players || []) {
    const pos = p.position || "—";
    (grouped[pos] = grouped[pos] || []).push(p);
  }
  const positions = Object.keys(grouped).sort((a, b) => {
    const rank = (pos) => {
      const i = positionsOrder.indexOf(pos);
      return i === -1 ? 999 : i;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
  });

  // Sort within each position by depth, then last name
  for (const pos of positions) {
    grouped[pos].sort((a, b) =>
      (a.depth || 1) - (b.depth || 1) || (a.last_name || "").localeCompare(b.last_name || "")
    );
  }

  // Summary counts
  const counts = { present: 0, late: 0, excused: 0, absent: 0, unmarked: 0 };
  for (const p of roster.players || []) {
    const s = attendance?.[p.id]?.status;
    if (s && counts[s] !== undefined) counts[s] += 1;
    else counts.unmarked += 1;
  }
  const total = roster.players.length;
  const marked = total - counts.unmarked;

  return (
    <div className="mt-10 attendance-signin" data-testid="print-attendance">
      <style>{`
        @media print {
          .attendance-signin { page-break-before: always; }
        }
      `}</style>

      <div className="flex items-end justify-between border-b-2 border-black pb-2">
        <div>
          <div className="font-display text-xl uppercase tracking-wider">Attendance Sign-In</div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-neutral-600 mt-0.5">
            Field-ready roster · Mark in box: P Present · L Late · E Excused · A Absent
          </div>
        </div>
        <div className="text-right text-[10px] font-condensed uppercase tracking-widest text-neutral-600">
          <div>{total} on roster · {marked} pre-marked</div>
          <div className="mt-0.5">
            <span className="text-neutral-800">{counts.present}P</span>
            <span className="mx-2">·</span>
            <span className="text-neutral-800">{counts.late}L</span>
            <span className="mx-2">·</span>
            <span className="text-neutral-800">{counts.excused}E</span>
            <span className="mx-2">·</span>
            <span className="text-neutral-800">{counts.absent}A</span>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {positions.map((pos) => (
          <div key={pos} className="border border-neutral-400 avoid-page-break" data-testid={`print-attendance-${pos}`}>
            <div className="bg-neutral-100 border-b border-neutral-400 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] flex items-center justify-between">
              <span>{pos} <span className="font-normal text-neutral-500">— {grouped[pos].length}</span></span>
            </div>
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr className="border-b border-neutral-400 bg-neutral-50">
                  <th className="text-left py-1 px-2 w-6">#</th>
                  <th className="text-left py-1 px-2">Player</th>
                  <th className="text-center py-1 px-1 w-6" title="Present"><Box>P</Box></th>
                  <th className="text-center py-1 px-1 w-6" title="Late"><Box>L</Box></th>
                  <th className="text-center py-1 px-1 w-6" title="Excused"><Box>E</Box></th>
                  <th className="text-center py-1 px-1 w-6" title="Absent"><Box>A</Box></th>
                  <th className="text-left py-1 px-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {grouped[pos].map((pl, i) => {
                  const status = attendance?.[pl.id]?.status;
                  return (
                    <tr key={pl.id} className="border-b border-neutral-300">
                      <td className="py-1 px-2 text-neutral-500">{i + 1}</td>
                      <td className="py-1 px-2 font-semibold">
                        {pl.last_name}, {pl.first_name}
                        {pl.depth > 1 && (
                          <span className="text-[9px] font-normal text-neutral-500 ml-1">({pl.depth}{pl.depth === 2 ? "nd" : "rd"})</span>
                        )}
                        {!pl.ad_cleared && (
                          <span className="text-[9px] font-bold text-neutral-800 ml-2 border border-neutral-800 px-1">NOT AD-CLEARED</span>
                        )}
                      </td>
                      <StatusCell active={status === "present"} />
                      <StatusCell active={status === "late"} />
                      <StatusCell active={status === "excused"} />
                      <StatusCell active={status === "absent"} />
                      <td className="py-1 px-2 border-l border-neutral-300"></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Sign-off row */}
      <div className="mt-6 grid grid-cols-[2fr_1fr] gap-6">
        <div>
          <div className="border-b border-black h-8" />
          <div className="text-[9px] font-condensed uppercase tracking-[0.25em] text-neutral-600 mt-1">Head Coach Signature</div>
        </div>
        <div>
          <div className="border-b border-black h-8" />
          <div className="text-[9px] font-condensed uppercase tracking-[0.25em] text-neutral-600 mt-1">Date</div>
        </div>
      </div>
    </div>
  );
}

function Box({ children }) {
  return (
    <span className="inline-block w-4 h-4 border border-neutral-800 leading-4 text-center font-bold text-[9px]">
      {children}
    </span>
  );
}

function StatusCell({ active }) {
  return (
    <td className="py-1 px-1 text-center">
      <span
        className={`inline-block w-4 h-4 border ${
          active ? "bg-neutral-800 border-neutral-900 text-white" : "border-neutral-500 text-neutral-500"
        } leading-4 text-center font-bold text-[9px]`}
      >
        {active ? "✓" : ""}
      </span>
    </td>
  );
}
